import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addInterest, addSkill, deleteInterest, deleteSkill, fetchInterests, fetchSkills, selectInterests, selectInterestsError, selectInterestsLoading, selectSkills, selectSkillsError, selectSkillsLoading, updateInterest, updateSkill } from '../redux/AttributesSlice';
import { Oval } from 'react-loader-spinner';
import { toast } from 'react-toastify';
import { IoClose } from 'react-icons/io5';

const Attributes = () => {
  const dispatch = useDispatch();

  const skills = useSelector(selectSkills);
  const isLoadingSkills = useSelector(selectSkillsLoading);
  const skillsError = useSelector(selectSkillsError);

  const interests = useSelector(selectInterests);
  const isLoadingInterests = useSelector(selectInterestsLoading);
  const interestsError = useSelector(selectInterestsError);

  const [activeTab, setActiveTab] = useState('skills');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newItem, setNewItem] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchSkills());
    dispatch(fetchInterests());
  }, [dispatch]);

  const handleAdd = async () => {
    if (!newItem.trim()) return;
    setIsLoading(true);
    try {
      if (activeTab === 'skills') {
        await dispatch(addSkill({ name: newItem.trim() })).unwrap();
      } else {
        await dispatch(addInterest({ name: newItem.trim() })).unwrap();
      }
      setNewItem('');
      setShowAddModal(false);
      toast.success('Added Successfully');
    } catch (error) {
      toast.error('Failed to add');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setIsDeleting(false);
    setEditedName(selectedItem.name);
  };

  const handleDeleteClick = () => {
    setIsDeleting(true);
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!editedName.trim()) return;
    setIsLoading(true);
    try {
      const updatedItem = { ...selectedItem, name: editedName.trim() };
      if (activeTab === 'skills') {
        await dispatch(updateSkill(updatedItem)).unwrap();
      } else {
        await dispatch(updateInterest(updatedItem)).unwrap();
      }
      toast.success('Updated Successfully');
      setShowEditModal(false);
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      if (activeTab === 'skills') {
        await dispatch(deleteSkill(selectedItem.id)).unwrap();
      } else {
        await dispatch(deleteInterest(selectedItem.id)).unwrap();
      }
      toast.success('Deleted Successfully');
      setShowEditModal(false);
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setEditedName(item.name);
    setIsEditing(false);
    setIsDeleting(false);
    setShowEditModal(true);
  };
  const handleCancel = () =>{
    if(isDeleting){
      setIsDeleting(false)
    }else if(isEditing){
      setIsEditing(false)
    }
  }

  const filteredItems = activeTab === 'skills'
    ? skills?.filter(skill => skill.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : interests?.filter(interest => interest.name.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">My Attributes</h1>
          <p className="text-gray-600 mt-2">Manage your skills and interests</p>
        </div>

        {/* Tabs */}
        <div className="flex mb-6 bg-white rounded-lg shadow p-1">
          <button
            onClick={() => setActiveTab('skills')}
            className={`flex-1 py-3 px-6 rounded-md transition-all ${activeTab === 'skills'
              ? 'bg-[#1330C5] text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
              }`}
          >
            Skills
          </button>
          <button
            onClick={() => setActiveTab('interests')}
            className={`flex-1 py-3 px-6 rounded-md transition-all ${activeTab === 'interests'
              ? 'bg-[#1330C5] text-white shadow-md'
              : 'text-gray-600 hover:bg-gray-100'
              }`}
          >
            Interests
          </button>
        </div>

        {/* Search and Add */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-[#1330C5] focus:ring-2 focus:ring-blue-200 outline-none transition-all"
            />
            <svg
              className="absolute left-3 top-3.5 h-5 w-5 text-gray-400"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 bg-[#1330C5] text-white rounded-lg hover:bg-blue-600 transition-colors shadow-md"
          >
            Add New
          </button>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow p-4">
          {isLoadingSkills || isLoadingInterests ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#1330C5] border-t-transparent"></div>
            </div>
          ) : interestsError ? (
            <div className="p-8 text-center text-red-500">
              Failed to load data. Please try again later.
            </div>
          ) : filteredItems?.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No items found. Add some new ones!
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {filteredItems?.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between py-2 px-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors duration-200 cursor-pointer"
                onClick={() => handleItemClick(item)}
              >
                <div className="flex items-center">
                  <div className={`w-2 h-2 rounded-full ${activeTab === 'skills' ? "bg-green-500" : "bg-blue-500"} mr-2`}></div>
                  <span className="text-gray-700 font-medium text-sm">{item.name}</span>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </div>

      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className='flex justify-end'>
              <IoClose 
              onClick={()=>setShowEditModal(false)}
              size={24} className='hover:scale-105 hover:cursor-pointer'/>
            </div>
            <h2 className="text-xl font-bold mb-4">
              {activeTab === 'skills' ? 'Skill' : 'Interest'} Details
            </h2>
            
            {isEditing ? (
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className="w-full px-4 py-2 border rounded-md mb-4"
                autoFocus
              />
            ) : (
              <p className="text-gray-700 text-sm mb-4 bg-gray-100 p-2 rounded-md">{selectedItem.name}</p>
            )}

            {isDeleting && (
              <p className="text-sm text-red-500 mb-4">
                Are you sure you want to delete this {activeTab === 'skills' ? 'skill' : 'interest'}?
              </p>
            )}

            <div className="flex justify-end gap-2">
              {!isEditing && !isDeleting && (
                <>
                  <button
                    onClick={handleEditClick}
                    className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={handleDeleteClick}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  >
                    Delete
                  </button>
                </>
              )}

              {isEditing && (
                <>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="px-4 py-2 bg-[#1330C5] text-white rounded-md hover:bg-blue-600 transition-colors"
                  >
                    {isLoading ? (
                      <Oval visible={true} height="20" width="20" color="#ffff" ariaLabel="oval-loading" />
                    ) : 'Save Changes'}
                  </button>
                </>
              )}

              {isDeleting && (
                <>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    {isLoading ? (
                      <Oval visible={true} height="20" width="20" color="#ffff" ariaLabel="oval-loading" />
                    ) : 'Confirm Delete'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              Add New {activeTab === 'skills' ? 'Skill' : 'Interest'}
            </h2>
            <input
              type="text"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              className="w-full px-4 py-2 border rounded-md mb-4"
              placeholder={`Enter ${activeTab === 'skills' ? 'skill' : 'interest'} name`}
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                className="px-4 py-2 bg-[#1330C5] text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                {isLoading ? (
                  <div className=" flex justify-center items-center">
                    <Oval
                      visible={true}
                      height="20"
                      width="20"
                      color="#ffff"
                      ariaLabel="oval-loading"
                      wrapperStyle={{}}
                      wrapperClass=""
                    />
                  </div>
                ) : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Attributes;